
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, OnInit, Optional, Output, signal, viewChild } from "@angular/core";
import { cloneDeep } from "lodash";
import { combineLatest, EMPTY, Observable, of, Subscription } from "rxjs";
import { catchError, filter, map, switchMap, take, tap } from "rxjs/operators";
import { ICharacteristicData } from "../../../models/i-characteristic-data";
import { ICharacteristicMetaDataCollection } from "../../../models/i-characteristic-meta-data-collection";
import { IEntity } from "../../../models/i-entity";
import { IRecordPresetSelection } from "../../../models/i-record-preset-selection";
import { IRecordTitle } from "../../../models/i-record-title";
import { CharTypeId, TemplateGroupIDs, VirtualCharTagLabels } from "../../../rl-common.consts";
import { DealService } from "../../../services/deal/deal.service";
import { IEntityRelationshipState } from "../../../services/entity/entity-relationship.models";
import { SelectState } from "../../../services/entity/entity.messages";
import { EntityService } from "../../../services/entity/entity.service";
import { ISaveCharDataRequest } from "../../../services/entity/models/i-save-char-data-request";
import { ParentEntityService } from "../../../services/entity/parent-entity/parent-entity.service";
import { IGridViewAssocEntityRec } from "../../../services/grid-view/models/i-grid-view-assoc-entity-rec";
import { GrowlerService } from "../../../services/growler.service";
import { ModDetailService } from "../../../services/mod-detail/mod-detail.service";
import { ModalBuilder } from "../../../services/modal-builder/modal-builder";
import { OneConfigService } from "../../../services/one-config/one-config.service";
import { ProductApprovalsService } from "../../../services/product-approvals/product-approvals.service";
import { PromptService } from "../../../services/prompt/prompt.service";
import { HttpStatusCode } from "../../../services/rl-http.models";
import { SearchService } from "../../../services/search/search.service";
import { SessionService } from "../../../services/session.service";
import { TablesService } from "../../../services/tables/tables.service";
import { CharacteristicUtil, TagLabel } from "../../../utils/characteristic.util";
import { IDUtil } from "../../../utils/id.util";
import { QueryUtil } from "../../../utils/query.util";
import { ComponentRelationshipsComponent } from "../../associations/entity-relationships/component-relationships/component-relationships.component";
import { CharDataTableComponent } from "../../char-data/char-data-table.component";
import { SearchFieldNames } from "../../entities/entity-search/query.models";
import { EditEntityUtils } from "./edit-entity.utils";
import { EditWarningModalComponent } from "./edit-warning-modal/edit-warning-modal.component";
import { EditEntityCompleteEvent } from "./models/edit-entity-complete-event";

export interface CharTypeRelChange {
	charTypeId: CharTypeId;
	changeWasMade: boolean;
	wasAdded: boolean;
	wasDeleted: boolean;
}

@Component({
	selector: "rl-edit-entity",
	templateUrl: "./edit-entity.component.html",
	styleUrls: ["./edit-entity.component.scss"],
	changeDetection: ChangeDetectionStrategy.Eager,
	imports: [ComponentRelationshipsComponent, CharDataTableComponent]
})
export class EditEntityComponent implements OnInit, OnDestroy {
	@Input()
	editingEntityId: string;

	@Input()
	baseEntityId?: string;

	@Input()
	parentEntityId?: string;

	@Input()
	isRelative = false;

	@Input()
	templateGroupId?: number;

	@Output()
	onComplete = new EventEmitter<EditEntityCompleteEvent>();

	@Output()
	onCancel = new EventEmitter<void>();

	relationships: { [charTypeId: number]: IRecordTitle[] } = {};
	entity: IEntity;
	/** persistPresets: the record's saved preset links; null when the feature is off */
	presetSelections = signal<IRecordPresetSelection[]>(null);
	table = viewChild(CharDataTableComponent);
	template: ICharacteristicMetaDataCollection;
	charData: ICharacteristicData[];
	initialCharData: ICharacteristicData[];

	isSaving = false;
	charTypeId: number;
	recordId: number;
	editingRelRecId: number;
	parentRelRecId: number;
	isMultiple = true;
	requiredCharTypeDict: { [charTypeId: number]: boolean } = {};
	selectedStateDictionary: { [charTypeId: number]: IEntityRelationshipState<number, IGridViewAssocEntityRec> } = {};
	defaultSelectStateDict: { [charTypeId: number]: SelectState<number, IGridViewAssocEntityRec> } = {};
	subs: Subscription[] = [];

	private readonly _tablesAndRights = [CharTypeId.Table, CharTypeId.Right];

	get isTablesOrRights() {
		return this._tablesAndRights.includes(this.charTypeId);
	}

	constructor(
		private readonly _parentEntityService: ParentEntityService,
		private readonly _entityService: EntityService,
		private readonly _sessionService: SessionService,
		private readonly _oneConfigService: OneConfigService,
		private readonly _modalBuilder: ModalBuilder,
		private readonly _growlerService: GrowlerService,
		@Optional() private readonly _modDetailService: ModDetailService,
		private readonly _tableService: TablesService,
		private readonly _productApprovalsService: ProductApprovalsService,
		private readonly _searchService: SearchService,
		private readonly _dealService: DealService,
		private readonly _promptService: PromptService
	) { }

	ngOnInit() {
		const editingEntityId = IDUtil.splitEntityID(this.editingEntityId);
		this.recordId = editingEntityId.recID;
		this.charTypeId = editingEntityId.charTypeID;
		this.isMultiple = this.charTypeId !== CharTypeId.Amount;

		const getEditEntityMetaData$ = this._entityService.getEditEntityMetaData(this.editingEntityId, this.baseEntityId, this.parentEntityId);
		const getCharData$ = this._entityService.getCharData(this.editingEntityId);

		const sub = combineLatest([getEditEntityMetaData$, getCharData$])
			.pipe(
		).subscribe(([entityData, cd]) => {
			this.charData = cd;
			this.entity = entityData.entityData.entity;
			this.presetSelections.set(entityData.entityData.presetSelections ?? null);
			const tmd = this._oneConfigService.getTemplateMetaData(this.charTypeId, entityData.entityData.entity.templateID);
			this.template = tmd;
			this.initialCharData = cloneDeep(entityData.entityData.characteristicData);
			this.relationships = entityData.assocEntities;
			this.editingRelRecId = entityData.editingRelRecId;
			this.parentRelRecId = entityData.parentRelRecId;
			this.requiredCharTypeDict = entityData.requiredCharTypeDict;
			this.defaultSelectStateDict = entityData.defaultSelectStateDict;
			this._parentEntityService.initEditParent(this.editingEntityId, this.parentEntityId, this.baseEntityId, this.parentRelRecId, this.editingRelRecId, this.template?.templateID);
		});

		this.subs.push(sub);
	}

	save() {
		this.isSaving = true;

		const entityId = this.editingEntityId;
		const charData = cloneDeep(this.charData);
		const editedCharDataRequest = EditEntityUtils.getEditedCharData(this._oneConfigService, this.initialCharData, charData, this.template.characteristicMetaDatas);

		const amortStartDateError = EditEntityUtils.validateAmortizationStartDate(this.template, this.initialCharData, charData);
		if (amortStartDateError) {
			this._growlerService.error("Error").growl(amortStartDateError);
			this.isSaving = false;
			return;
		}
		const recalculateAmortization = !!EditEntityUtils.getChangedAmortizationStartDate(this.template, this.initialCharData, charData);

		const templateGroupId = this.template.template.templateGroupID;
		const cmd = this.template.characteristicMetaDatas.find(cmd => cmd.tagLabel === VirtualCharTagLabels.Title);
		if (templateGroupId === TemplateGroupIDs.ProductApprovalsTemplateGroupId && this.charTypeId === CharTypeId.Document) {
			if (!editedCharDataRequest.charDatas.find(cd => cd.charactersticID === cmd.characteristicID)) {
				editedCharDataRequest.charDatas = [...editedCharDataRequest.charDatas, ...charData.filter(cd => cd.charactersticID === cmd.characteristicID)];
			}
		}

		const relChangesWereMade = this.relChangesWereMade();
		const charDataChangesWereMade = this.charDataChangesWereMade(editedCharDataRequest);
		const presetSelectionChanges = this.presetSelections() ? this.table()?.presetSelectionChanges ?? [] : [];
		const changesWereMade = relChangesWereMade || charDataChangesWereMade || presetSelectionChanges.length > 0;
		if (!changesWereMade) {
			this.onComplete.emit({
				success: true,
				entityId: this.parentEntityId,
				relWasAdded: false,
				relWasDeleted: false,
				charDataChangesWereMade: false,
				anyChangesWereMade: false,
				relChangesWereMade: false
			});
			return;
		}
		const parentEntityId = this._parentEntityService.parent ?
			IDUtil.toID(this._sessionService.divId, this._parentEntityService.parent.charTypeId, this._parentEntityService.parent.recordId) :
			null;
		const relNetChanges = this.selectedStateDictionary;
		if (this.relChangesWereMade()) {
			// TODO: refactor this so that we don't have modals opening modals
			const confirmationSub = this._modalBuilder.build<EditWarningModalComponent>(EditWarningModalComponent)
				.open(comp => {
					comp.selectedStateDictionary = this.selectedStateDictionary;
					comp.charTypeId = this.charTypeId;
					return comp.onConfirm;
				}).pipe(
					switchMap(performSave => {
						if (performSave) {
							return this.saveContinue$(entityId, editedCharDataRequest, parentEntityId, presetSelectionChanges, recalculateAmortization);
						} else {
							this.isSaving = false;
							return of();
						}
					}),
					catchError((err) => {
						this.isSaving = false;
						throw err;
					})
				)
				.subscribe();
			this.subs.push(confirmationSub);
		} else {
			const saveSub = this.saveContinue$(entityId, editedCharDataRequest, parentEntityId, presetSelectionChanges, recalculateAmortization).subscribe();
			this.subs.push(saveSub);
		}
	}

	private getEmitEvent(): EditEntityCompleteEvent {
		const charData = cloneDeep(this.charData);
		const editedCharDataRequest = EditEntityUtils.getEditedCharData(this._oneConfigService, this.initialCharData, charData, this.template.characteristicMetaDatas);
		const relChanges = this.getRelChanges();
		const charDataChangesWereMade = this.charDataChangesWereMade(editedCharDataRequest);
		const relChangesWereMade = relChanges.some(c => c.changeWasMade);
		const changesWereMade = relChangesWereMade || charDataChangesWereMade;
		return {
			success: true,
			entityId: this.parentEntityId,
			relWasAdded: relChanges.some(c => c.wasAdded),
			relWasDeleted: relChanges.some(c => c.wasDeleted),
			charDataChangesWereMade,
			relChangesWereMade,
			anyChangesWereMade: changesWereMade,
		};
	}

	private saveContinue$(entityId: string, editedCharDataRequest: ISaveCharDataRequest,
		parentEntityId: string = null, presetSelections: IRecordPresetSelection[] = [], recalculateAmortization = false) {
		const split = IDUtil.splitEntityID(entityId);
		const emitEvent = this.getEmitEvent();

		return this.mergeProductApprovalsPdfIfNeeded(this.entity.charTypeID, this.entity.templateID, editedCharDataRequest)
			.pipe(
				switchMap((updatedCharDataRequest: ISaveCharDataRequest) =>
					this.allowAmountToSaveContinue(split.charTypeID, split.recID, updatedCharDataRequest)
						.pipe(
							switchMap(allowSave => {
								if (!allowSave) {
									this.isSaving = false;
								}
								return of(allowSave);
							}),
							filter(allowSaveToCont => allowSaveToCont),
							switchMap(() => this._entityService.saveEntity(split.charTypeID, split.recID, updatedCharDataRequest, this.selectedStateDictionary, parentEntityId, presetSelections)
								.pipe(
									tap(() => {
										if (recalculateAmortization) {
											this.recalculateAmortization();
										}
									}),
									catchError(e => {
										if (e.status === HttpStatusCode.Conflict) {
											this._growlerService.error().growl(e?.error?.message);
											return of(null);
										}
										throw e;
									}),
									tap((result) => {
										this.onComplete.emit(emitEvent);
										this.isSaving = false;
									})
								))
						)
				)
			);
	}

	/**
	 * Recalculate amortization for the related Allocation and refreshes the results.
	 */
	private recalculateAmortization() {
		const dealEntityId = this.baseEntityId;
		const baseEntityId = dealEntityId ? IDUtil.splitEntityID(dealEntityId) : null;
		if (baseEntityId?.charTypeID !== CharTypeId.Deal) {
			return;
		}

		const amortizationModel = CharacteristicUtil.getCharacteristic(TagLabel.AmortizationModel, this.template, this.charData)?.value ?? "";
		const isExactStraightLine = amortizationModel.indexOf("Daily") > 0;

		const parentRelQuery = QueryUtil.$and(
			QueryUtil.$eq(SearchFieldNames.Relationship.childRecordID, this.recordId),
			QueryUtil.$eq(SearchFieldNames.Relationship.childCharTypeID, this.charTypeId),
			QueryUtil.$eq(SearchFieldNames.Relationship.parentCharTypeID, this.charTypeId)
		);
		const divId = this._sessionService.divId;
		const charTypeId = this.charTypeId;
		this._searchService.search(CharTypeId.Relationship, "", parentRelQuery, { rows: 1 }).pipe(
			map(results => results.documents[0]?.characteristics[SearchFieldNames.Relationship.parentRecordID] as number),
			filter(parentRecordId => !!parentRecordId),
			switchMap(parentRecordId => this._dealService.calculateAmortization(dealEntityId, -1, [IDUtil.toID(divId, charTypeId, parentRecordId)], isExactStraightLine)),
			switchMap(jobResponse => this._promptService.jobProgress(jobResponse.jobId, true)),
			take(1),
			catchError(() => {
				this._growlerService.error("Error").growl("Unable to recalculate amortization.");
				return EMPTY;
			})
		).subscribe(() => {
			window.location.reload();
		});
	}

	private mergeProductApprovalsPdfIfNeeded(charTypeID: number, templateID: number, charDataRequest: ISaveCharDataRequest): Observable<ISaveCharDataRequest> {
		const isProductApprovalsDocument =
			this.template?.template?.templateGroupID === TemplateGroupIDs.ProductApprovalsTemplateGroupId &&
			this.charTypeId === CharTypeId.Document;

		if (!isProductApprovalsDocument || !charDataRequest.charDatas?.length) {
			return of(charDataRequest);
		}

		return this._productApprovalsService.mergeFilesToPdf(charTypeID, templateID, charDataRequest.charDatas).pipe(
			map(mergedCharData => {
				charDataRequest.charDatas = mergedCharData;
				return charDataRequest;
			})
		);
	}

	private allowAmountToSaveContinue(charTypeId: number, recordId: number, editedCharDataRequest: ISaveCharDataRequest): Observable<boolean> {
		if (charTypeId === CharTypeId.Amount && this._modDetailService) {
			const parentCharTypeId = this._modDetailService.entity.charTypeId;
			const amountCurrency = CharacteristicUtil.getLocalCurrency(charTypeId, this.template.characteristicMetaDatas, editedCharDataRequest.charDatas);
			const amountCurrencySymbol = amountCurrency[0];
			const amountCurrencyId = amountCurrency[1];
			if (parentCharTypeId == CharTypeId.Invoice) {
				if (amountCurrencySymbol) {
					const json = `[{"recID":${recordId},"curSym": "${amountCurrencySymbol}"}]`;
					return this._entityService.checkAmountInvoiceCurrencyMisMatch(charTypeId, json)
						.pipe(
							map(result => {
								if (result && result.length > 0 && amountCurrencySymbol !== result[0].invoiceCurSym) {
									this._growlerService.error("Error").growl(`Amount's selected Currency ${amountCurrencySymbol} does not match with associated Invoice Currency ${result[0].invoiceCurSym}`);
									return false;
								}
								return true;
							})
						);
				}
			} else if (parentCharTypeId == CharTypeId.Deal && amountCurrencyId) {
				return this._tableService.canCreateChildAmount(this._parentEntityService.parent?.parent?.recordId, amountCurrencyId, recordId)
					.pipe(
						tap(result => {
							if (!result) {
								this._growlerService.error().growl("Amounts of mixed currencies not allowed under single table row");
							}
						})
					);
			}
		}
		return of(true);
	}

	changesWereMade(editedCharDataRequest: ISaveCharDataRequest) {
		return this.relChangesWereMade() || this.charDataChangesWereMade(editedCharDataRequest);
	}

	relChangesWereMade() {
		return Object.values(this.selectedStateDictionary)
			.some(state => {
				if (state.netChanges.isAllDeselected) {
					return state.defaultSelectedIds.length > 0;
				} else if (!state.netChanges.isAllSelected) {
					return state.netChanges?.addedIds?.size > 0 || state.netChanges?.deletedIds?.size > 0;
				}
				const wasAllSelectedBefore = state.defaultSelectedIds.length === state.gridCount;
				return !wasAllSelectedBefore || state.netChanges.deletedIds.size > 0;
			});
	}

	getRelChanges(): CharTypeRelChange[] {
		return Object.entries(this.selectedStateDictionary)
			.map(kvp => {
				const ct = +kvp[0];
				const state = kvp[1];
				let wasAdded = false;
				let wasDeleted = false;
				if (state.netChanges.isAllDeselected) {
					wasAdded = state.defaultSelectedIds.length > 0;
				} else if (!state.netChanges.isAllSelected) {
					wasAdded = state.netChanges?.addedIds?.size > 0;
					wasDeleted = state.netChanges?.deletedIds?.size > 0;
				} else {
					const wasAllSelectedBefore = state.defaultSelectedIds.length === state.gridCount;
					wasDeleted = !wasAllSelectedBefore || state.netChanges.deletedIds.size > 0;
				}
				return {
					charTypeId: ct,
					changeWasMade: wasAdded || wasDeleted,
					wasAdded,
					wasDeleted
				}
			});
	}

	charDataChangesWereMade(editedCharDataRequest: ISaveCharDataRequest) {
		return editedCharDataRequest.charDatas.length > 0 || editedCharDataRequest.deletedRecCharIds.length > 0 ||
			editedCharDataRequest.deletedDateMathCalcIds.length > 0 || editedCharDataRequest.deletedDateMathRelIds.length > 0;
	}

	cancel() {
		this.onCancel.emit();
	}

	updateRelationshipStates(states: { [charTypeId: number]: IEntityRelationshipState<number, IGridViewAssocEntityRec> }) {
		this.selectedStateDictionary = states;
	}

	get areRelsValid() {
		const invalidRequired = Object.keys(this.requiredCharTypeDict ?? {})
			.filter(charTypeId => this.selectedStateDictionary[charTypeId]?.isValid === false);
		return invalidRequired.length === 0;
	}

	ngOnDestroy(): void {
		this.subs.forEach(sub => sub.unsubscribe());
	}
}
